import { useRef } from 'react';

import { useMutation, useQuery } from '@apollo/client';

import { closeWsConnection } from 'common/apollo/PhoenixSocketHelper';
import useDialog from 'common/components/core/DialogProvider/useDialog';
import { useEvent } from 'common/components/hooks/useEvent/useEvent';
import { LogoutMutation } from 'common/graphql/mutations/authentication';
import { clearTokenAndNavigate, clearTokenAndReload } from 'common/helpers/authenticationHelper';
import { getTokenDuration } from 'common/helpers/jwtHelper';
import { safeTimeout } from 'common/helpers/timeoutHelper';

import { getTokenInfo } from './queries';
import sessionDialogOpenSubject from './sessionDialogOpenSubject';
import SessionExpiredDialog from './SessionExpiredDialog/SessionExpiredDialog';
import SessionTimeoutWarningDialog, {
  SESSION_WARNING_DURATION,
} from './SessionTimeoutWarningDialog/SessionTimeoutWarningDialog';

export type UseAutomaticLogoutInput = {
  token: string;
};

/**
 * A hook that handles automatic logout functionality.
 */
export default function useAutomaticLogout({ token }: UseAutomaticLogoutInput) {
  const { openDialog, closeDialog } = useDialog();
  const { data } = useQuery(getTokenInfo);
  const [logout] = useMutation(LogoutMutation);

  const openSessionExpiredDialog = useEvent(() => {
    closeWsConnection();

    let onLogin;
    let onClose = () => sessionDialogOpenSubject.next(false);
    let disableLoginAction;
    isWarningDialogOpen.current = false;

    // Check for custom auth flow assertions
    if (data?.me?.assertions) {
      const assertions = JSON.parse(data.me.assertions);
      const logoutUrl = assertions?.frame_logout_url ? decodeURIComponent(assertions.frame_logout_url) : null;
      const loginUrl = assertions?.frame_login_url ? decodeURIComponent(assertions.frame_login_url) : null;

      const loginRedirectUrl = loginUrl || `${data.me?.loginUrl}&return_url=${window.location.href}`;

      onLogin = loginRedirectUrl ? () => clearTokenAndNavigate(loginRedirectUrl) : undefined;
      onClose = () => {
        // Always notify that the dialog is closed
        sessionDialogOpenSubject.next(false);

        if (logoutUrl) {
          clearTokenAndNavigate(logoutUrl);
        } else {
          clearTokenAndReload();
        }
      };
      disableLoginAction = !!logoutUrl && !loginUrl;
    }

    openDialog(SessionExpiredDialog, {
      onLogin,
      onClose,
      disableLoginAction,
    });

    sessionDialogOpenSubject.next(true);
  });

  const logoutOnTimeout = useEvent(async () => {
    try {
      await logout();
    } catch (error) {
      // This is a fire-and-forget mutation so it does not need error handling atm
    } finally {
      openSessionExpiredDialog();
    }
  });

  const openSessionWarningDialog = useEvent(() => {
    isWarningDialogOpen.current = true;

    const onExtendSession = async () => {
      isWarningDialogOpen.current = false;
      closeDialog();
      scheduleLogOut();
    };

    openDialog(SessionTimeoutWarningDialog, {
      onExtendSession,
    });
  });

  const logoutTimeoutId = useRef<number | undefined>();
  const warningTimeoutId = useRef<number | undefined>();
  const isWarningDialogOpen = useRef(false);

  const scheduleLogOut = useEvent(() => {
    // Don't reschedule if the warning dialog is already showing,
    if (isWarningDialogOpen.current) {
      return;
    }

    window.clearTimeout(logoutTimeoutId.current);
    window.clearTimeout(warningTimeoutId.current);

    const tokenDuration = getTokenDuration(token);

    warningTimeoutId.current = window.setTimeout(
      openSessionWarningDialog,
      safeTimeout(tokenDuration - SESSION_WARNING_DURATION)
    );

    logoutTimeoutId.current = window.setTimeout(logoutOnTimeout, safeTimeout(tokenDuration));
  });

  return { scheduleLogOut };
}
